// DOCS: https://github.com/IdentityModel/oidc-client-js/wiki
import { UserManager, WebStorageStateStore, UserManagerSettings, User } from 'oidc-client'

import store from '@/store/store'
import { SET_TOKEN, USER_SETDATA, USER_SET_RULES } from '@/store/MUTATIONS-TYPES'
import { GuardianService } from './GuardianService'
import { Http } from './http'
import { IRefreshTokenResponse } from '@/interfaces/IRefreshTokenResponse'
import { LOCAL_STORAGE } from '@/utils/LSUtil'
import { IAuthService } from '@/interfaces/IAuthService'
import { ConfigCatHelper } from '@/utils/ConfigCatHelper'
import { User as UserIntf } from '@/interfaces'

const http = Http.create(import.meta.env.VUE_APP_API_AUTH)

async function createSettings(): Promise<UserManagerSettings> {
	const isOpaEnabled = await ConfigCatHelper.getIsOpaEnabled()
	const baseScope = 'openid profile email atta.profile offline_access'
	const scope = isOpaEnabled ? `${baseScope} atta:any` : baseScope

	return {
		userStore: new WebStorageStateStore({ store: window.localStorage }),
		authority: import.meta.env.VUE_APP_API_AUTH,
		client_id: 'ISolve',
		client_secret: 'secret',
		redirect_uri: `${window.location.protocol}//${window.location.host}/#/login/authorize`,
		silent_redirect_uri: `${window.location.protocol}//${window.location.host}/#/login/silent-refresh`,
		response_type: 'code',
		automaticSilentRenew: true,
		scope,
		loadUserInfo: true,
		post_logout_redirect_uri: `${window.location.protocol}${window.location.host}`
	}
}

export class LegacyAuthService implements IAuthService {
	private _userManager!: UserManager

	get userManager(): UserManager {
		if (!this._userManager) {
			throw new Error('LegacyAuthService not initialized. Call init() first.')
		}
		return this._userManager
	}

	async init(): Promise<void> {
		const settings = await createSettings()
		this._userManager = new UserManager(settings)
		this._userManager.events.addUserLoaded(function (user) {})
	}

	async getUser(): Promise<User | null> {
		return await this.userManager.getUser()
	}

	async login(): Promise<void> {
		localStorage.setItem(LOCAL_STORAGE.APP_CURRENT_ROUTE_BEFORE_AUTHORIZE, window.location.hash)
		return await this.userManager.signinRedirect()
	}

	async logout(): Promise<void> {
		// clean cookies and localstorage to remove traces
		var cookies = document.cookie.split(';')
		for (var i = 0; i < cookies.length; i++) {
			var cookie = cookies[i]
			var eqPos = cookie.indexOf('=')
			var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
			document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT'
		}
		localStorage.clear()

		await this.userManager.signoutRedirect({
			userStore: new WebStorageStateStore({ store: window.localStorage }),
			post_logout_redirect_uri: `${window.location.protocol}${window.location.host}`
		})
	}

	/**
	 * Define os dados do usuário para toda a aplicação
	 */
	async setUserStore(user: User | null = null): Promise<User | null> {
		if (user == null) user = await this.getUser()

		if (user != null) {
			const { commit } = store
			commit(USER_SETDATA, user)

			try {
				var response = await GuardianService.getCurrentUserRules()
				commit(USER_SET_RULES, response.result)
			} catch (error) {
				localStorage.clear()

				commit(USER_SETDATA, null)
				await this.login()
				await this.setUserStore()
			}

			return user
		}

		return null
	}

	async authorize(): Promise<User> {
		return await new UserManager({
			response_mode: 'query',
			userStore: new WebStorageStateStore({ store: window.localStorage })
		}).signinRedirectCallback()
	}

	async authorizeByEncryptedId(id: string): Promise<string> {
		var response = await http.post<string>(`/account/AuthorizeById?idEncrypt=${id}`)
		return response.data
	}

	async GetUserByEncryptedId(id: string): Promise<User> {
		var result = await this.authorizeByEncryptedId(id)
		var user = await this.getUserByToken(result)
		user.access_token = result
		await this.setUserStore(user)

		return user
	}

	async getUserByToken(token: string): Promise<User> {
		var response = await http.get<User>(`/connect/userinfo`, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		})

		return response.data
	}

	async refreshToken(): Promise<any> {
		var response = await http.post<IRefreshTokenResponse>(
			`/connect/token`,
			{
				params: {
					client_id: 'ISolve',
					grant_type: 'refresh_token',
					//client_secret: 'secret',
					refresh_token: store.state.user?.refreshToken
				}
			},
			{
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
			}
		)

		const { commit } = store
		commit(SET_TOKEN, response)

		return response
	}

	async setPassword(cpf: string): Promise<any> {
		var response = await http.get(`/account/new-user-password/${cpf}`)
		return response.data
	}

	/**
	 * Initialize user context for OIDC auth flow (legacy)
	 */
	async initializeUserContext(): Promise<void> {
		const { commit } = store

		let user = await this.getUser()

		if (!user && !window.location.href.includes('/#/login')) {
			if (!window.location.href.includes('/#/login/authorize') && !window.location.href.includes('/#/login/silent-refresh')) {
				await this.login()
			}
		}

		if (window.location.href.includes('/#/login/authorize')) {
			user = await this.authorize()
			commit(USER_SETDATA, user)
		}

		if (user) await this.setUserStore()

		const response = await GuardianService.getCurrentUserRules()
		if (!response.error) {
			commit(USER_SET_RULES, response.result)
		}
	}

	async setActiveUserAccount(user: UserIntf.IUserAccount): Promise<void> {
		console.error('LegacyAuthService: setActiveUserAccount() is not available in legacy auth.')
	}
}
